import React from "react";
import { motion } from "framer-motion";
import { Loader2, Mail } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { RankEmblem } from "../rank/RankEmblem";
import { t, useLang } from "../../lib/i18n";

/**
 * An invitation waiting for the player (backend/social.py; the poll is `useInvites` in ./invites.js):
 * who invites, their rank, the time left, « Refuser » / « Rejoindre ». On the Lobby's « Jouer » (the
 * newest) and « Social » (all) tabs. The app shows no toasts: it is a card of its own. `busy`: the
 * player sits in a game (they must leave it first).
 */
export function InviteCard({ invite, onAnswer, acting, error, busy = false, more = 0 }) {
  useLang();
  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/15 to-slate-900/90 px-3 py-2 shadow-[0_6px_24px_rgba(0,0,0,0.45)]"
      data-testid="invite-banner"
    >
      <div className="flex items-center gap-3">
        <span className="relative shrink-0">
          <Avatar className="h-9 w-9 ring-2 ring-amber-400/60">
            <AvatarImage src={invite.from.picture} />
            <AvatarFallback className="bg-emerald-900 text-amber-300">{invite.from.name?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          {invite.from.rank && (
            <RankEmblem rank={invite.from.rank} size={18} className="absolute -bottom-1 -end-1.5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm text-white">
            <Mail className="h-3.5 w-3.5 shrink-0 text-amber-300" />
            <span className="truncate" data-testid="invite-banner-title">{t("invite.title", { name: invite.from.name })}</span>
          </div>
          <div className="text-[11px] text-slate-400">
            {t("invite.sub")} · {t("invite.expires", { n: Math.max(1, Math.ceil(invite.expires_in / 60)) })}
          </div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onAnswer(invite, false)}
          disabled={!!acting}
          data-testid="invite-decline"
          className="h-8 rounded-full border border-white/10 bg-slate-900/70 text-sm text-slate-300 transition-colors hover:bg-slate-800 disabled:opacity-50"
        >
          {acting === "decline" ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("invite.decline")}
        </button>
        <button
          type="button"
          onClick={() => onAnswer(invite, true)}
          disabled={!!acting || busy}
          title={busy ? t("invite.quitFirst") : undefined}
          data-testid="invite-join"
          className="h-8 rounded-full bg-amber-400 text-sm text-slate-900 transition-colors hover:bg-amber-300 disabled:opacity-40"
        >
          {acting === "join" ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("invite.join")}
        </button>
      </div>
      {(busy || error) && (
        <p className={`mt-2 text-center text-xs ${error ? "text-rose-300" : "text-slate-400"}`}>{error || t("invite.quitFirst")}</p>
      )}
      {more > 0 && <p className="mt-1.5 text-center text-[11px] text-slate-500">{t("invite.more", { n: more })}</p>}
    </motion.div>
  );
}

export default InviteCard;
